import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { ReviewSubmissionForm } from "@/components/property/review-submission-form";
import { useSEO } from "@/lib/seo-utils";

interface ReviewPrefill {
  name?: string;
  email?: string;
  checkin?: string;
  checkout?: string;
}

export default function SubmitReviewPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = params.get("token");

  // The email link carries only an opaque token; redeem it for the guest's
  // prefill data (never passed through the URL).
  const { data: tokenPrefill } = useQuery<ReviewPrefill>({
    queryKey: ["/api/review-prefill", token],
    queryFn: async () => {
      const res = await fetch(`/api/review-prefill/${encodeURIComponent(token!)}`);
      if (!res.ok) return {};
      return res.json();
    },
    enabled: !!token,
    staleTime: Infinity,
  });

  const prefill: ReviewPrefill = tokenPrefill || {};

  const fromEmailLink = Boolean(token && prefill.email);

  useSEO({
    title: "Submit a Review | The Kualoa Beach House",
    description: "Share your experience at our Ka'a'awa beachfront vacation rental.",
    robots: "noindex, nofollow",
  });

  return (
    <div className="min-h-screen bg-shell">
      <Header />
      <main id="main-content" className="pt-24 pb-16 sm:pt-28">
        <div className="container mx-auto px-4 sm:px-6 max-w-2xl">
          <h1 className="text-[32px] leading-tight sm:text-luxury-3xl font-serif font-normal text-forest mb-2 tracking-luxury-tight text-center">
            Share Your Stay
          </h1>
          <p className="text-ink-soft font-sans text-center mb-6 sm:mb-8 max-w-xl mx-auto">
            Your feedback helps future guests discover our Ka'a'awa beach house. Thank you for taking a moment to review your stay.
          </p>
          <ReviewSubmissionForm
            standalone
            prefill={prefill}
            startAtStep2={fromEmailLink}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
